"use client";

import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";
import { ROOM_PHOTOS, roomPhotoLayout } from "./room-photos";
import { setWallLayout, type SetRoomScene } from "./art-sets";

export interface SetWallPiece {
  src: string;
  alt: string;
  /** Trim of the chosen size, turned to suit this piece. */
  widthMm: number;
  heightMm: number;
}

export interface SetWallProps {
  pieces: SetWallPiece[];
  initialScene?: SetRoomScene;
  captions: Record<SetRoomScene, string>;
  sceneLabels: Record<SetRoomScene, string>;
  /** Small, unswitchable version for cards: plain wall, no caption. */
  thumbnail?: boolean;
}

const SCENES: SetRoomScene[] = ["sofa", "bed", "plain"];

function useElementWidth() {
  const ref = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const update = () => setWidth(el.clientWidth);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return { ref, width };
}

/** Group width may use at most this share of the room the photo shows. */
const MAX_GROUP_SHARE = 0.9;
/** On the plain wall, the group fills this much of the box. */
const PLAIN_FILL = 0.84;
/** Thumbnails are a strip; the group may run nearly edge to edge. */
const THUMB_FILL_W = 0.94;
/** Extra height the room window is framed for; see the layout below. */
const FURNITURE_CONTEXT_CM = 30;

/**
 * The whole set on one wall, every piece at the size chosen.
 *
 * The same rooms and the same centimetre-to-pixel rule as the single-work
 * preview (room-photos.ts), with the group framed as one piece: the window
 * closes in on it vertically, and if the group is wider than the wall that
 * window would show — three 60 cm prints on a phone — the room is drawn
 * smaller rather than the outer pieces cut off. Scale stays honest either
 * way: the sofa and the prints shrink together.
 */
export default function SetWall({
  pieces,
  initialScene = "sofa",
  captions,
  sceneLabels,
  thumbnail = false,
}: SetWallProps) {
  const [scene, setScene] = useState<SetRoomScene>(thumbnail ? "plain" : initialScene);
  const { ref, width } = useElementWidth();
  const group = useMemo(() => setWallLayout(pieces), [pieces]);

  // Same cap as the single-work preview: never above 520 px or 95% of the width.
  const maxHeight = thumbnail
    ? Math.round(width * 0.42)
    : Math.min(520, Math.round((width || 320) * 0.95));
  const photo = scene === "plain" ? null : ROOM_PHOTOS[scene];

  const layout = useMemo(() => {
    if (!photo || width <= 0 || group.widthCm <= 0) return null;
    // Framed as if the group were a little taller than it is, so the window
    // opens down onto more of the furniture: a set spans most of the wall,
    // and without the sofa in shot nothing says how big that is.
    const piece = { widthCm: group.widthCm, heightCm: group.heightCm + FURNITURE_CONTEXT_CM };
    const first = roomPhotoLayout(photo, width, maxHeight, piece);
    if (!first) return null;
    const groupPx = group.widthCm * first.pxPerCm;
    if (groupPx <= first.sceneWidth * MAX_GROUP_SHARE) return first;
    // Too wide for the wall in shot: pull the camera back.
    const shrink = (first.sceneWidth * MAX_GROUP_SHARE) / groupPx;
    return roomPhotoLayout(photo, width, maxHeight * shrink, piece) ?? first;
  }, [photo, width, maxHeight, group]);

  const plainPxPerCm =
    width > 0 && group.widthCm > 0 && group.heightCm > 0
      ? Math.min(
          (width * (thumbnail ? THUMB_FILL_W : PLAIN_FILL)) / group.widthCm,
          (maxHeight * PLAIN_FILL) / group.heightCm,
        )
      : 0;

  const pxPerCm = layout ? layout.pxPerCm : plainPxPerCm;
  const sceneWidth = layout ? layout.sceneWidth : width;
  const sceneHeight = layout ? layout.sceneHeight : maxHeight;
  const groupW = group.widthCm * pxPerCm;
  const groupH = group.heightCm * pxPerCm;
  const groupTop =
    layout && photo
      ? layout.floorY - photo.hangCentreCm * pxPerCm - groupH / 2
      : (sceneHeight - groupH) / 2;
  const depth = Math.max(2, Math.round((pieces[0]?.widthMm ?? 0) / 10 * pxPerCm * 0.02));

  return (
    <Box>
      <Box ref={ref} sx={{ width: "100%" }}>
        <Box
          sx={{
            position: "relative",
            width: sceneWidth ? `${sceneWidth}px` : "100%",
            mx: "auto",
            height: pxPerCm ? sceneHeight : thumbnail ? 140 : 320,
            // A clean white surface, not a grey "wall": the prints and their
            // shadows are the only thing on it.
            bgcolor: "background.paper",
            borderRadius: 2,
            overflow: "hidden",
          }}
        >
          {layout && photo && (
            /* eslint-disable-next-line @next/next/no-img-element */
            <img
              src={photo.src}
              alt=""
              aria-hidden
              style={{
                position: "absolute",
                left: layout.img.left,
                top: layout.img.top,
                width: layout.img.width,
                height: layout.img.height,
                maxWidth: "none",
                userSelect: "none",
                pointerEvents: "none",
              }}
            />
          )}

          {pxPerCm > 0 &&
            pieces.map((p, i) => {
              const at = group.pieces[i];
              return (
                <Box
                  key={`${p.src}-${i}`}
                  sx={{
                    position: "absolute",
                    left: (sceneWidth - groupW) / 2 + at.left * pxPerCm,
                    top: groupTop + at.top * pxPerCm,
                    width: at.width * pxPerCm,
                    height: at.height * pxPerCm,
                    boxShadow: thumbnail
                      ? "0 6px 12px -6px rgba(20,24,32,.45)"
                      : "0 18px 34px -14px rgba(20,24,32,.45)",
                  }}
                >
                  <Box
                    component="img"
                    src={p.src}
                    alt={p.alt}
                    loading="lazy"
                    sx={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                  />
                  {/* The wrapped edge, as on CanvasObject. */}
                  <Box
                    aria-hidden
                    sx={{
                      position: "absolute",
                      top: 0,
                      right: -depth,
                      width: depth,
                      height: "100%",
                      background: "linear-gradient(90deg, rgba(0,0,0,.22), rgba(0,0,0,.42))",
                      transform: "skewY(-7deg)",
                      transformOrigin: "left top",
                    }}
                  />
                </Box>
              );
            })}
        </Box>
      </Box>

      {!thumbnail && (
        <Box sx={{ mt: 1.5, display: "flex", flexDirection: "column", alignItems: "center", gap: 1 }}>
          <ToggleButtonGroup
            exclusive
            value={scene}
            onChange={(_, next) => next && setScene(next as SetRoomScene)}
          >
            {SCENES.map((value) => (
              <ToggleButton
                key={value}
                value={value}
                sx={{ px: 2.5, py: 0.75, fontSize: 15, fontWeight: 700, textTransform: "none" }}
              >
                {sceneLabels[value]}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
          <Typography variant="caption" color="text.secondary">
            {captions[scene]}
          </Typography>
        </Box>
      )}
    </Box>
  );
}
